/**
 * SEO landing pages for "best X" high-intent keywords.
 * Routes: /tools/best-password-generator, best-currency-converter, best-productivity-tools
 */

export type BestToolTopic =
  | 'best-password-generator'
  | 'best-currency-converter'
  | 'best-productivity-tools';

export interface BestToolSEOPageConfig {
  topic: BestToolTopic;
  title: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  h1: string;
  subtitle: string;
  intro: string;
  features: string[];
  faqs: { question: string; answer: string }[];
  ctaText: string;
  ctaHref: string;
}

const BASE_URL = 'https://focuzon.com';

export const BEST_TOOL_TOPICS: BestToolTopic[] = [
  'best-password-generator',
  'best-currency-converter',
  'best-productivity-tools',
];

export const BEST_TOOL_SEO_CONFIG: Record<BestToolTopic, BestToolSEOPageConfig> = {
  'best-password-generator': {
    topic: 'best-password-generator',
    title: 'Best Password Generator – Strong, Secure & Free | Focuzon',
    metaDescription:
      'Generate strong, secure passwords instantly. Best free password generator with length control, uppercase, numbers & symbols. No signup. Use the best password generator online.',
    ogTitle: 'Best Password Generator – Free & Secure | Focuzon',
    ogDescription: 'Generate strong, secure passwords in one click. Free password generator.',
    h1: 'Best Password Generator',
    subtitle: 'Generate strong, secure passwords in seconds. Free, no signup.',
    intro:
      'The best password generator makes it easy to create strong, unique passwords for every account. Ours lets you choose length and include uppercase, numbers, and symbols, then copy the result with one click. No signup, no tracking, just a reliable free tool.',
    features: [
      'Adjustable length (e.g. 8–32+ characters)',
      'Include uppercase, lowercase, numbers, and symbols',
      'One-click copy to clipboard',
      'No account or signup required',
      'Runs in your browser; nothing is stored or sent to a server',
    ],
    faqs: [
      {
        question: 'What is the best password generator?',
        answer:
          "A good password generator creates random, strong passwords with letters, numbers, and symbols. Focuzon's free tool lets you set length and options and copy the result instantly. Strong passwords reduce breach risk. Use different passwords for each service. Look for browser-based generators that do not store or transmit data. Focuzon runs locally with no signup.",
      },
      {
        question: 'Is this password generator safe?',
        answer:
          'Yes. The generator runs entirely in your browser. Passwords are created locally using secure random algorithms and are not sent to or stored on any server. Nothing is logged or transmitted. You can verify by disconnecting the internet and generating; it will still work. This approach is recommended by security experts for password generation.',
      },
      {
        question: 'Is the password generator free?',
        answer: 'Yes. It is free to use with no signup or limits. No credit card or account required. Generate as many passwords as you need. The tool works immediately in your browser. Focuzon offers it as part of a suite of free productivity tools for focus and work.',
      },
      {
        question: 'Why use Focuzon for password generation?',
        answer: 'Focuzon runs the generator entirely in your browser with no data stored or sent. You get adjustable length, character options, and one-click copy. It works alongside focus timers and other free productivity tools. No signup means no barrier to generating strong passwords when you need them.',
      },
    ],
    ctaText: 'Generate a strong password',
    ctaHref: '/tools/password-generator',
  },
  'best-currency-converter': {
    topic: 'best-currency-converter',
    title: 'Best Currency Converter – Live Exchange Rates (USD, INR, EUR) | Focuzon',
    metaDescription:
      'Convert currencies with live exchange rates. Best free currency converter for USD to INR, EUR, GBP and more. Accurate, fast, no signup. Try the best currency converter online.',
    ogTitle: 'Best Currency Converter – Live Rates | Focuzon',
    ogDescription: 'Convert currencies with live rates. Free USD to INR, EUR, GBP and more.',
    h1: 'Best Currency Converter',
    subtitle: 'Convert with live exchange rates. USD, INR, EUR, GBP, and more. Free, no signup.',
    intro:
      'The best currency converter gives you accurate, up-to-date rates without signup. Focuzon’s free tool uses live exchange rates so you can convert USD to INR, EUR to GBP, and other major pairs in one place. Fast, simple, and free.',
    features: [
      'Live exchange rates from a trusted source',
      'Major currencies: USD, EUR, GBP, INR, JPY, AUD, CAD, CHF',
      'Clear “today’s rate” display with date',
      'No signup or account required',
      'Works on desktop and mobile',
    ],
    faqs: [
      {
        question: 'What is the best currency converter?',
        answer:
          "A reliable currency converter uses live or recent exchange rates and supports major currencies. Focuzon's free tool shows today's rates for USD, INR, EUR, GBP, and more. Enter an amount and select from and to currencies to see the result. Useful for travel, payments, or pricing. No signup required. Works on desktop and mobile. Rates are updated regularly for accuracy.",
      },
      {
        question: 'Are the exchange rates live?',
        answer: 'Yes. The tool fetches current or recent exchange rates from trusted sources so you see up-to-date conversions. Rates change throughout the day; the tool reflects the latest available data. Check the date shown with the rate. For large transfers, verify with your bank. Focuzon updates rates regularly.',
      },
      {
        question: 'Is the currency converter free?',
        answer: 'Yes. It is free to use with no signup or limits. No credit card or account required. Convert as often as you need. The tool works in your browser. Supports USD, EUR, GBP, INR, JPY, AUD, CAD, and CHF. Focuzon offers it with other free productivity tools. Free for personal and business use.',
      },
      {
        question: 'Why use Focuzon for currency conversion?',
        answer: 'Focuzon provides live exchange rates for major currencies in one place. No signup, no limits. The tool is fast and works on mobile. It sits alongside free focus timers, password generator, and word counter. Useful for travel, freelance billing, or quick conversions when you need accurate rates.',
      },
    ],
    ctaText: 'Convert currency now',
    ctaHref: '/tools/currency-converter/usd-to-inr',
  },
  'best-productivity-tools': {
    topic: 'best-productivity-tools',
    title: 'Best Free Productivity Tools Online – Invoices, Calculators, Converters | Focuzon',
    metaDescription:
      'Best free productivity tools in one place: invoice generator, password generator, currency converter, EMI calculator, word counter, and more. No signup. Start with the best productivity tools.',
    ogTitle: 'Best Productivity Tools – Free Online | Focuzon',
    ogDescription: 'Free productivity tools: invoices, calculators, converters. No signup.',
    h1: 'Best Productivity Tools',
    subtitle: 'Free tools for freelancers and teams: invoices, calculators, converters, and more.',
    intro:
      'The best productivity tools save time without costing a fortune. Focuzon brings together free tools you can use right away: generate passwords, convert currencies, calculate EMI, count words, and more. No signup required. Just pick a tool and start.',
    features: [
      'Password generator – strong, secure passwords',
      'Currency converter – live USD, INR, EUR, and more',
      'EMI calculator – loan EMI, SIP, lump sum, flat rate',
      'Profit margin calculator – margin and markup',
      'Word counter, unit converter, and more',
    ],
    faqs: [
      {
        question: 'What are the best free productivity tools?',
        answer:
          'Useful free productivity tools include password generators, currency converters, word counters, and calculators. Focuzon offers these in one place with no signup required. Each tool runs in your browser. Use them for passwords, conversions, word counts, QR codes, and more. No credit card or subscription. Pick a tool and start. Optional account creation is for focus timers and task management.',
      },
      {
        question: 'Do I need to sign up to use these tools?',
        answer: 'No. Most tools work without an account. Password generator, currency converter, word counter, unit converter, and QR code generator all work immediately. No email or login required. Optional account creation enables focus timers, task management, and session history. Start using any tool right away. No barriers to access.',
      },
      {
        question: 'Are these tools really free?',
        answer: 'Yes. All listed tools are free to use with no subscription or credit card required. No hidden fees or premium tiers for basic functionality. Generate passwords, convert currencies, count words, and more at no cost. Focuzon offers them as part of a free productivity platform. Use as often as you need.',
      },
      {
        question: 'Can I use these tools for work or business?',
        answer: 'Yes. All Focuzon tools are free for personal and business use. Password generator, currency converter, word counter, and calculators work without signup. Optional account creation enables focus timers and task management for productivity. No restrictions on commercial use for the free tools.',
      },
    ],
    ctaText: 'Browse all free tools',
    ctaHref: '/tools',
  },
};

export function getBestToolConfig(topic: string): BestToolSEOPageConfig | null {
  if (BEST_TOOL_TOPICS.includes(topic as BestToolTopic)) {
    return BEST_TOOL_SEO_CONFIG[topic as BestToolTopic];
  }
  return null;
}

export function getBestToolCanonicalUrl(topic: BestToolTopic): string {
  return `${BASE_URL}/tools/${topic}`;
}
